import { formatSiteDateTime } from '@/lib/site-datetime'

export type MarketInstrumentKey =
  | 'USDTRY'
  | 'EURTRY'
  | 'GBPTRY'
  | 'BIST'
  | 'GRAMALTINTRY'
  | 'CEYREKALTINTRY'
  | 'BRENT'
  | 'BTCUSDT'
  | 'ETHUSDT'
  | 'XRPUSDT'

export type MarketHistoryRange = '1d' | '7d' | '30d' | '1y'

export type MarketRateSnapshot = {
  symbol: string
  quote: string
  rate: number
  buy?: number | null
  sell?: number | null
  id?: number
  changePct: number | null
  fetchedAt?: string
}

export type MarketPayload = Record<MarketInstrumentKey, MarketRateSnapshot | null>

export type MarketInstrumentMeta = {
  key: MarketInstrumentKey
  label: string
  /** Detay sayfası başlığı */
  displayTitle: string
  symbol: string
  slug: string
  href: string
  decimals: number
  quote: string
}

export type MarketPreset = 'home' | 'forex' | 'crypto' | 'gold'

export function marketInstrumentHref(slug: string) {
  return `/ekonomi/${slug}`
}

export const MARKET_INSTRUMENTS: Record<MarketInstrumentKey, MarketInstrumentMeta> = {
  USDTRY: {
    key: 'USDTRY',
    label: 'DOLAR',
    displayTitle: 'Amerikan Doları',
    symbol: 'USD',
    slug: 'dolar',
    href: marketInstrumentHref('dolar'),
    decimals: 2,
    quote: 'TRY',
  },
  EURTRY: {
    key: 'EURTRY',
    label: 'EURO',
    displayTitle: 'Euro',
    symbol: 'EUR',
    slug: 'euro',
    href: marketInstrumentHref('euro'),
    decimals: 2,
    quote: 'TRY',
  },
  GRAMALTINTRY: {
    key: 'GRAMALTINTRY',
    label: 'ALTIN',
    displayTitle: 'Gram Altın',
    symbol: 'XAU',
    slug: 'gram_altin',
    href: marketInstrumentHref('gram_altin'),
    decimals: 2,
    quote: 'TRY',
  },
  BIST: {
    key: 'BIST',
    label: 'BIST 100',
    displayTitle: 'BIST 100',
    symbol: 'BIST',
    slug: 'bist',
    href: marketInstrumentHref('bist'),
    /** Endeks puanı — Yahoo XU100.IS */
    decimals: 2,
    quote: 'INDEX',
  },
  GBPTRY: {
    key: 'GBPTRY',
    label: 'STERLİN',
    displayTitle: 'İngiliz Sterlini',
    symbol: 'GBP',
    slug: 'sterlin',
    href: marketInstrumentHref('sterlin'),
    decimals: 2,
    quote: 'TRY',
  },
  CEYREKALTINTRY: {
    key: 'CEYREKALTINTRY',
    label: 'ÇEYREK',
    displayTitle: 'Çeyrek Altın',
    symbol: 'XAUQ',
    slug: 'ceyrek_altin',
    href: marketInstrumentHref('ceyrek_altin'),
    decimals: 2,
    quote: 'TRY',
  },
  BRENT: {
    key: 'BRENT',
    label: 'BRENT',
    displayTitle: 'Brent Petrol',
    symbol: 'BRENT',
    slug: 'brent-petrol',
    href: marketInstrumentHref('brent-petrol'),
    decimals: 2,
    quote: 'USD',
  },
  BTCUSDT: {
    key: 'BTCUSDT',
    label: 'BITCOIN',
    displayTitle: 'Bitcoin',
    symbol: 'BTC',
    slug: 'bitcoin',
    href: marketInstrumentHref('bitcoin'),
    decimals: 2,
    quote: 'USDT',
  },
  ETHUSDT: {
    key: 'ETHUSDT',
    label: 'ETHEREUM',
    displayTitle: 'Ethereum',
    symbol: 'ETH',
    slug: 'ethereum',
    href: marketInstrumentHref('ethereum'),
    decimals: 2,
    quote: 'USDT',
  },
  XRPUSDT: {
    key: 'XRPUSDT',
    label: 'RIPPLE',
    displayTitle: 'Ripple (XRP)',
    symbol: 'XRP',
    slug: 'ripple',
    href: marketInstrumentHref('ripple'),
    decimals: 4,
    quote: 'USDT',
  },
}

export const MARKET_PRESETS: Record<MarketPreset, MarketInstrumentKey[]> = {
  home: ['USDTRY', 'EURTRY', 'GRAMALTINTRY', 'BIST', 'GBPTRY', 'CEYREKALTINTRY', 'BRENT', 'BTCUSDT', 'ETHUSDT', 'XRPUSDT'],
  forex: ['USDTRY', 'EURTRY', 'GBPTRY'],
  crypto: ['BTCUSDT', 'ETHUSDT', 'XRPUSDT'],
  gold: ['GRAMALTINTRY', 'CEYREKALTINTRY'],
}

export const MARKET_PAYLOAD_KEYS = Object.keys(MARKET_INSTRUMENTS) as MarketInstrumentKey[]

const TAG_TO_INSTRUMENTS: Record<string, MarketInstrumentKey[]> = {
  dolar: ['USDTRY'],
  euro: ['EURTRY'],
  sterlin: ['GBPTRY'],
  altin: ['GRAMALTINTRY'],
  gram_altin: ['GRAMALTINTRY'],
  'gram-altin': ['GRAMALTINTRY'],
  ceyrek_altin: ['CEYREKALTINTRY'],
  'ceyrek-altin': ['CEYREKALTINTRY'],
  bitcoin: ['BTCUSDT'],
  btc: ['BTCUSDT'],
  ethereum: ['ETHUSDT'],
  eth: ['ETHUSDT'],
  ripple: ['XRPUSDT'],
  xrp: ['XRPUSDT'],
  bist: ['BIST'],
  brent: ['BRENT'],
  'brent-petrol': ['BRENT'],
  petrol: ['BRENT'],
  ekonomi: ['USDTRY', 'EURTRY', 'GRAMALTINTRY'],
}

function normalizeMarketTagKey(raw: string): string {
  return raw
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
}

export type ArticleMarketTag = { slug?: string; tag?: string }

export function mapArticleTagsForMarket(
  tags?: Array<{ tag?: { slug?: string | null; tag?: string | null } | null } | null>,
): ArticleMarketTag[] {
  return (tags ?? [])
    .map((row) => ({
      slug: row?.tag?.slug ?? undefined,
      tag: row?.tag?.tag ?? undefined,
    }))
    .filter((t) => t.slug || t.tag)
}

export function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') {
    const cleaned = v
      .trim()
      .replace('%', '')
      .replace(/\s+/g, '')
      .replace(',', '.')
      .replace('−', '-')
      .replace(/^\+/, '')
    const n = Number(cleaned)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function normalizeMarketRow(row: unknown): MarketRateSnapshot | null {
  if (!row || typeof row !== 'object') return null
  const r = row as Record<string, unknown>
  const rate = toNum(r.rate)
  const changePct = toNum(r.changePct ?? r.change_pct)
  return {
    symbol: typeof r.symbol === 'string' ? r.symbol : '',
    quote: typeof r.quote === 'string' ? r.quote : '',
    rate: rate ?? 0,
    buy: toNum(r.buy),
    sell: toNum(r.sell),
    id: typeof r.id === 'number' ? r.id : toNum(r.id) ?? undefined,
    changePct,
    fetchedAt: typeof r.fetchedAt === 'string' ? r.fetchedAt : undefined,
  }
}

export function normalizeMarketPayload(payload: unknown): MarketPayload | null {
  if (!payload || typeof payload !== 'object') return null
  const out = {} as MarketPayload
  for (const key of MARKET_PAYLOAD_KEYS) {
    out[key] = normalizeMarketRow((payload as Record<string, unknown>)[key])
  }
  return out
}

export function formatRate(symbol: string, rate: number, decimals?: number) {
  if (!Number.isFinite(rate) || rate === 0) return '----'
  const digits =
    decimals ??
    (symbol === 'XRP' ? 4 : symbol === 'BTC' || symbol === 'ETH' ? 2 : symbol === 'BIST' ? 2 : 2)
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(rate)
}

/** Detay kartı: alış / satış — yalnızca gerçek spread varsa */
export type MarketHeroPriceLayout =
  | { mode: 'dual'; buy: number; sell: number }
  | { mode: 'single'; price: number }

export function getMarketHeroPriceLayout(
  key: MarketInstrumentKey,
  row: MarketRateSnapshot | null | undefined,
): MarketHeroPriceLayout | null {
  if (!row || !Number.isFinite(row.rate) || row.rate === 0) return null

  const meta = getInstrumentMeta(key)
  const singlePriceKeys: MarketInstrumentKey[] = ['BIST', 'BRENT']

  const buyRaw = row.buy != null && Number.isFinite(row.buy) && row.buy > 0 ? row.buy : null
  const sellRaw = row.sell != null && Number.isFinite(row.sell) && row.sell > 0 ? row.sell : null

  const epsilon = Math.max(Math.abs(row.rate) * 1e-8, 10 ** -(meta.decimals + 2))
  const hasSpread =
    buyRaw != null && sellRaw != null && Math.abs(sellRaw - buyRaw) > epsilon

  if (singlePriceKeys.includes(key) || !hasSpread) {
    return { mode: 'single', price: row.rate }
  }

  return { mode: 'dual', buy: buyRaw, sell: sellRaw }
}

export function resolveBuySell(
  row: MarketRateSnapshot | null | undefined,
): { buy: number | null; sell: number | null } {
  if (!row || !Number.isFinite(row.rate) || row.rate === 0) {
    return { buy: null, sell: null }
  }
  const buy = row.buy != null && Number.isFinite(row.buy) ? row.buy : row.rate
  const sell = row.sell != null && Number.isFinite(row.sell) ? row.sell : row.rate
  return { buy, sell }
}

export function formatMarketUpdatedAt(iso?: string | null): string {
  if (!iso) return '—'
  return formatSiteDateTime(iso, {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatChangeBadge(pct: number | null | undefined) {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) {
    return { text: '—%', up: false, down: false, className: 'bg-gray-400' }
  }
  const formatted = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(pct))
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : ''
  return {
    text: `${sign}${formatted}%`,
    up: pct >= 0,
    down: pct < 0,
    className: pct >= 0 ? 'bg-emerald-700' : 'bg-red-700',
  }
}

export function resolveInstrumentsFromTags(
  tags: Array<{ slug?: string; tag?: string }>,
): MarketInstrumentKey[] {
  const keys = new Set<MarketInstrumentKey>()
  for (const t of tags) {
    const raw = (t.slug ?? t.tag ?? '').trim()
    if (!raw) continue
    const slug = normalizeMarketTagKey(raw)
    for (const key of TAG_TO_INSTRUMENTS[slug] ?? []) keys.add(key)
  }
  return [...keys]
}

export function resolveInstrumentKeys(options: {
  instruments?: MarketInstrumentKey[]
  preset?: MarketPreset
  tags?: Array<{ slug?: string; tag?: string }>
}): MarketInstrumentKey[] {
  if (options.instruments?.length) return options.instruments
  if (options.tags?.length) {
    const fromTags = resolveInstrumentsFromTags(options.tags)
    if (fromTags.length) return fromTags
  }
  if (options.preset) return MARKET_PRESETS[options.preset]
  return MARKET_PRESETS.home
}

export function getInstrumentMeta(key: MarketInstrumentKey): MarketInstrumentMeta {
  return MARKET_INSTRUMENTS[key]
}

const USD_PRICED_INSTRUMENTS = new Set<MarketInstrumentKey>([
  'BTCUSDT',
  'ETHUSDT',
  'XRPUSDT',
  'BRENT',
])

export function usesDollarQuoteDisplay(key: MarketInstrumentKey): boolean {
  return USD_PRICED_INSTRUMENTS.has(key)
}

/** @deprecated use usesDollarQuoteDisplay */
export function usesDollarPrefix(key: MarketInstrumentKey): boolean {
  return usesDollarQuoteDisplay(key)
}

export function isIndexInstrument(key: MarketInstrumentKey): boolean {
  return key === 'BIST'
}

/** Kart/şerit etiketi — endeks puan / TL / $ */
export function formatQuoteLabel(meta: MarketInstrumentMeta): string {
  if (isIndexInstrument(meta.key) || meta.quote === 'INDEX') return 'Puan'
  if (usesDollarQuoteDisplay(meta.key)) return '$'
  if (meta.quote === 'TRY') return 'TL'
  if (meta.quote === 'USD') return '$'
  return meta.quote
}

/** Detay sayfası SEO başlığı */
export function getInstrumentPageTitle(meta: MarketInstrumentMeta): string {
  if (isIndexInstrument(meta.key)) return `${meta.label} Endeksi ve Grafiği`
  if (usesDollarQuoteDisplay(meta.key) || meta.key === 'BRENT') {
    return `${meta.label} Fiyatı ve Grafiği`
  }
  return `${meta.label} Kuru ve Grafiği`
}

export function getInstrumentPriceCaption(meta: MarketInstrumentMeta): string {
  const quote = formatQuoteLabel(meta)
  if (isIndexInstrument(meta.key)) return `Güncel Endeks (${quote})`
  return `Güncel Fiyat (${quote})`
}

export function formatInstrumentRate(
  key: MarketInstrumentKey,
  rate: number,
  decimals?: number,
): string {
  const meta = getInstrumentMeta(key)
  return formatRate(meta.symbol, rate, decimals ?? meta.decimals)
}
